// src/components/ResizableElement.jsx
import React, { useState, useRef, useEffect } from 'react';

const ResizableElement = ({ 
  value, 
  onChange, 
  position, 
  onPositionChange, 
  size,
  onSizeChange,
  className = '', 
  style = {},
  isEditable = true,
  children 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const parentRect = elementRef.current.parentElement.getBoundingClientRect();
        const newX = Math.max(0, e.clientX - parentRect.left - dragOffset.x);
        const newY = Math.max(0, e.clientY - parentRect.top - dragOffset.y);
        
        const maxX = parentRect.width - size.width;
        const maxY = parentRect.height - size.height;
        
        onPositionChange({
          x: Math.min(maxX, newX),
          y: Math.min(maxY, newY)
        });
      } else if (isResizing) {
        const rect = elementRef.current.getBoundingClientRect();
        const newWidth = Math.max(100, e.clientX - rect.left);
        const newHeight = Math.max(50, e.clientY - rect.top);
        onSizeChange({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      document.body.style.cursor = 'default';
    };

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isDragging ? 'grabbing' : 'resize';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
    };
  }, [isDragging, isResizing, dragOffset, size.width, size.height, onPositionChange, onSizeChange]);

  const handleDragStart = (e) => {
    e.stopPropagation();
    if (!isEditing) {
      const rect = elementRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
      document.body.style.cursor = 'grabbing';
    }
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    document.body.style.cursor = 'se-resize';
  };

  return (
    <div
      ref={elementRef}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        transition: isDragging || isResizing ? 'none' : 'box-shadow 0.2s ease',
        backgroundColor: 'white',
        ...style
      }}
      className={`
        relative group rounded
        ${isDragging || isResizing ? 'shadow-lg z-50' : 'shadow-sm'}
        ${isEditable ? 'hover:outline hover:outline-2 hover:outline-blue-200' : ''}
        ${className}
      `}
    >
      {isEditable && (
        <div
          className="absolute -top-6 left-0 right-0 h-6 flex justify-center items-center opacity-0 group-hover:opacity-100 cursor-move bg-white border rounded-t shadow-sm"
          onMouseDown={handleDragStart}
        >
          <div className="w-8 h-1 bg-gray-300 rounded-full"></div>
        </div>
      )}

      <div className="w-full h-full p-2">
        {isEditing && isEditable ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setIsEditing(false)}
            className="w-full h-full p-2 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        ) : (
          <div 
            className="w-full h-full overflow-auto"
            onDoubleClick={() => isEditable && onChange && setIsEditing(true)}
          >
            {children || value}
          </div>
        )}
      </div>

      {isEditable && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-0 group-hover:opacity-100"
          onMouseDown={handleResizeStart}
        >
          <div className="absolute bottom-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
        </div>
      )}
    </div>
  );
};

export default ResizableElement;